/**
 *
 * Questions
 *
 */

import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import Markdown from 'marked-react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { getQuestionVisibility } from 'containers/Assessment/utils';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { useSelector } from 'react-redux';
import PanelQuestion from './PanelQuestion';
import SingleQuestion from './SingleQuestion';
import SurveyContext from './SurveyContext';

const useStyles = makeStyles(theme => ({
  root: {
    borderTop: '1px solid #E8E8E8',
    borderBottom: '1px solid #E8E8E8',
  },
  instructionWrapper: {
    width: '100%',
    marginBottom: 8,
    padding: '16px 40px 0 40px',
    [theme.breakpoints.down('600')]: {
      padding: '16px 20px 0 20px',
    },
  },
  pageData: {
    width: '100%',
    padding: '20px 40px 0 40px',
    [theme.breakpoints.down('600')]: {
      padding: '20px 20px 0 20px',
    },
  },
  description: {
    marginTop: 8,
    fontSize: '1rem',
  },
}));

const Questions = ({ currentPage, pages }) => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    answers,
    error,
    onAnswerChange,
    onMatrixChange,
    onMatrixCheckboxChange,
    onCheckboxChange,
  } = useContext(SurveyContext);
  const clinicMode = useSelector(state => state.main.clinicMode);
  const [siteCopy] = useSiteCopySelector(['assessment']);

  const numberQuestionsPrevPages = useMemo(() => {
    let numQuestions = 0;
    for (let p = 0; p < pages.slice(0, currentPage).length; p += 1) {
      const flattedQuestions = [];
      pages[p].elements.forEach(el => {
        if (
          el.type === 'panel' &&
          getQuestionVisibility(el, answers) &&
          !!el.elements
        )
          flattedQuestions.push(...el.elements);
        else flattedQuestions.push(el);
      });
      numQuestions += flattedQuestions
        .filter(el => el.visible !== false)
        .filter(el => getQuestionVisibility(el, answers)).length;
    }
    return numQuestions;
  }, [pages, answers]);
  const pageQuestions = useMemo(() => {
    let cummulativeNumber = numberQuestionsPrevPages;
    return pages[currentPage].elements
      .filter(el => el.visible !== false)
      .filter(el => getQuestionVisibility(el, answers))
      .map(el => {
        const number = cummulativeNumber + 1;
        if (el.type === 'panel' && !!el.elements)
          cummulativeNumber += el.elements.filter(innerEl =>
            getQuestionVisibility(innerEl, answers),
          ).length;
        else cummulativeNumber += 1;

        return {
          ...el,
          number,
        };
      });
  }, [pages, answers, currentPage, numberQuestionsPrevPages]);
  const page = pages[currentPage];

  if (!page) return null;

  const surveyContextProps = {
    answers,
    error,
    onAnswerChange,
    onMatrixChange,
    onMatrixCheckboxChange,
    onCheckboxChange,
  };

  return (
    <Grid container className={classes.root}>
      {!clinicMode && _get(siteCopy, 'body') && (
        <Grid item xs={12} className={classes.instructionWrapper}>
          <RichTextReactRenderer
            data={_get(siteCopy, 'body.json')}
            paragraphVariant="h6"
            color={theme.palette.primary.main}
            withHeaderStyles
            centered
          />
        </Grid>
      )}
      {page.title && (
        <Grid item xs={12} className={classes.pageData}>
          <Typography variant="h6" color="textPrimary" align="justify">
            <Markdown isInline>{page.title}</Markdown>
          </Typography>
          {page.description && (
            <Typography
              color="textPrimary"
              align="justify"
              className={classes.description}
            >
              <Markdown isInline>{page.description}</Markdown>
            </Typography>
          )}
        </Grid>
      )}
      {pageQuestions.map(question =>
        question.type === 'panel' ? (
          <PanelQuestion
            key={question.name}
            surveyContextProps={surveyContextProps}
            {...question}
          />
        ) : (
          <SingleQuestion
            key={question.name}
            surveyContextProps={surveyContextProps}
            {...question}
          />
        ),
      )}
    </Grid>
  );
};

Questions.propTypes = {
  answers: PropTypes.object,
  currentPage: PropTypes.number,
  pages: PropTypes.array,
  error: PropTypes.array,
};

export default Questions;
